import React, { useState } from 'react';
import { Icons } from '../common';
import { generateQueryFilename } from '../../utils/queryFileGenerator';
import './InsertQueryButton.css';

/**
 * Button to insert queries from database into ZIP file
 */
const InsertQueryButton = ({ queries, articleId, zipFiles, onInsertQuery }) => {
  const [isInserting, setIsInserting] = useState(false);
  const [showQueryPreview, setShowQueryPreview] = useState(false);

  if (!queries || queries.length === 0) {
    return null;
  }

  const handleInsertQuery = async () => {
    if (!onInsertQuery) {
      console.error('onInsertQuery callback not provided');
      return;
    }

    setIsInserting(true);
    try {
      // Find manuscript file
      const manuscriptFile = zipFiles?.find(f =>
        f.name.toLowerCase().includes('manuscript') &&
        (f.type === 'docx' || f.type === 'doc')
      );

      if (!manuscriptFile) {
        throw new Error('Manuscript file not found in ZIP. Please ensure a manuscript file exists.');
      }

      // Remove duplicates and build query content
      const uniqueQueries = getUniqueQueries(queries);
      const duplicateCount = queries.length - uniqueQueries.length;
      const queryContent = generateSimpleQueryContent(queries);

      let successMessage;

      if (manuscriptFile.type === 'doc') {
        // Legacy binary .doc cannot be modified in the browser.
        // Instead, add the queries as a new .txt file inside the ZIP.
        const queryFilename = articleId
          ? `${articleId}_queries.txt`
          : 'queries.txt';

        const timestamp = new Date().toLocaleString();
        const fileContent = `VALIDATION QUERIES\nArticle: ${articleId || ''}\nGenerated: ${timestamp}\n${'='.repeat(60)}\n\n${queryContent}`;

        // 'text' type + new filename → creates a new file in the ZIP
        await onInsertQuery(queryFilename, fileContent, 'text', null);

        successMessage = `✅ ${uniqueQueries.length} ${uniqueQueries.length === 1 ? 'query' : 'queries'} saved as "${queryFilename}" inside the ZIP.`;
        if (duplicateCount > 0) {
          successMessage += `\n\n(${duplicateCount} duplicate ${duplicateCount === 1 ? 'query' : 'queries'} removed)`;
        }
        successMessage += '\n\nThe TE will find the query file alongside the manuscript.';
      } else {
        // .docx: modify the document directly with yellow highlight
        if (!manuscriptFile.zipEntry) {
          throw new Error('Manuscript file does not have zipEntry data');
        }
        const fileBuffer = await manuscriptFile.zipEntry.async('arraybuffer');
        await onInsertQuery(manuscriptFile.name, queryContent, 'docx', fileBuffer);

        successMessage = `✅ ${uniqueQueries.length} ${uniqueQueries.length === 1 ? 'query' : 'queries'} inserted into "${manuscriptFile.name}" successfully!`;
        if (duplicateCount > 0) {
          successMessage += `\n\n(${duplicateCount} duplicate ${duplicateCount === 1 ? 'query' : 'queries'} removed)`;
        }
        successMessage += '\n\nThe modified ZIP will be uploaded when assigned to a TE.';
      }

      alert(successMessage);
    } catch (error) {
      console.error('Error inserting query:', error);
      alert(`❌ Failed to insert query: ${error.message}`);
    } finally {
      setIsInserting(false);
    }
  };

  /**
   * Get unique queries by removing duplicates based on query text
   */
  const getUniqueQueries = (queries) => {
    const uniqueQueries = [];
    const seenQueryTexts = new Set();

    for (const query of queries) {
      const queryText = query.queryText?.trim();
      if (queryText && !seenQueryTexts.has(queryText)) {
        seenQueryTexts.add(queryText);
        uniqueQueries.push(query);
      }
    }

    // Log if duplicates were found
    if (uniqueQueries.length < queries.length) {
      console.log(`Removed ${queries.length - uniqueQueries.length} duplicate queries`);
    }

    return uniqueQueries;
  };

  /**
   * Generate simple query content (same format as manual query raising)
   * Just the query text, no headers or metadata
   * Removes duplicate queries
   */
  const generateSimpleQueryContent = (queries) => {
    const uniqueQueries = getUniqueQueries(queries);

    // Join all unique query texts with double line breaks
    // This matches the format used in AuthorQueryModal
    return uniqueQueries.map(query => query.queryText).join('\n\n');
  };

  /**
   * Generate formatted query content for preview modal
   * Includes headers and metadata for user to review
   */
  const generateQueryContentFromDatabase = (queries, articleId) => {
    const timestamp = new Date().toLocaleString();
    const header = `VALIDATION QUERIES FOR ARTICLE: ${articleId}
Retrieved from Database: ${timestamp}
${'='.repeat(60)}

`;

    const queryTexts = queries.map((query, index) => {
      const queryTypeLabel = formatQueryType(query.queryType);
      return `QUERY ${index + 1}: ${queryTypeLabel}
Author: ${query.authorName || 'N/A'}
Raised By: ${query.raisedByName || 'Unknown'}
Raised On: ${query.raisedOn ? new Date(query.raisedOn).toLocaleString() : 'N/A'}
Status: ${query.status?.toUpperCase() || 'OPEN'}

${query.queryText}`;
    }).join('\n\n' + '-'.repeat(60) + '\n\n');

    const footer = `

${'='.repeat(60)}
End of Validation Queries (${queries.length} total)
Retrieved from TE Reference Processing Tool Database`;

    return header + queryTexts + footer;
  };

  const formatQueryType = (type) => {
    const typeMap = {
      'copyright_no': 'Copyright Agreement Missing',
      'missing_in_system': 'Author Missing in System',
      'missing_in_manuscript': 'Author Missing in Manuscript',
      'validation_error': 'Validation Error'
    };
    return typeMap[type] || type;
  };

  return (
    <div className="insert-query-container">
      <button
        onClick={handleInsertQuery}
        disabled={isInserting}
        className="insert-query-button"
        title="Insert queries from database into ZIP file"
      >
        {isInserting ? (
          <>
            <Icons.LoaderIcon className="spinning" />
            <span>Inserting...</span>
          </>
        ) : (
          <>
            <Icons.DatabaseIcon />
            <span>Insert Query from Database</span>
            <span className="query-count-badge">{queries.length}</span>
          </>
        )}
      </button>

      <button
        onClick={() => setShowQueryPreview(!showQueryPreview)}
        className="preview-query-button"
        title="Preview queries"
      >
        <Icons.EyeIcon />
      </button>

      {showQueryPreview && (
        <div className="query-preview-modal">
          <div className="query-preview-content">
            <div className="query-preview-header">
              <h3>Database Queries Preview</h3>
              <button onClick={() => setShowQueryPreview(false)} className="close-button">
                ×
              </button>
            </div>
            <div className="query-preview-body">
              <pre>{generateQueryContentFromDatabase(queries, articleId)}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InsertQueryButton;

